import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { RELATION, USER_OWNERSHIP } from '../../connectionTypes'
import {
  addConnection,
  applyConnection,
  cancelConfirmNoConnections,
  confirmNoConnections,
  setProfileCaseIndex,
  unsetActiveConnection,
} from '../../redux/actions'
import {
  userHasConfirmedNoConnections,
  getActiveConnection,
  getProfileCasePagination,
  getProfileCaseIndex,
  getSelectedCase,
  isCancelConfirmNoConnectionsPending,
  isConfirmNoConnectionsPending,
} from '../../redux/selectors'
import AddConnectionButton from '../AddConnectionButton'
import ConnectionModal from '../ConnectionModal/ConnectionModal'
import I18n, { useI18nFunction } from '../I18n'
import InfoBox from '../InfoBox'
import ProfileCaseList from './ProfileCaseList'
import { isAdmin } from '../../redux/selectors'
import checkmark from '../../icons/checkmark.svg'
import attentionIcon from '../../icons/circled_alert_icon.svg'
import ListItemPlaceholder from '../ProfileCaseListItem/ListItemPlaceholder'

const ProfileCaseSection = ({
  isCurrentUsersProfile,
  connectionFormTabName,
  userId,
  connectionType,
  activeConnection,
  unsetActiveConnection,
  confirmNoConnections,
  hasConfirmedNoConnections,
  previousItems,
  currentItems,
  caseType,
  applicationItems,
  isConfirmNoConnectionsPending,
  cancelConfirmNoConnections,
  isCancelConfirmNoConnectionsPending,
  hasTransientConnection,
  hasApplicationComponents,
  items,
  applyConnection,
  addConnection,
  selectedCase,
  ownershipType,
  isCasesPending,
  casePagination,
  profileCaseIndex,
  setProfileCaseIndex,
  isAdmin,
  tabStats
}) => {
  const I18nFunction = useI18nFunction()
  const CURRENT = 'Current'
  const PREVIOUS = 'Previous'
  const APPLICATIONS = 'Applications'

  let sectionTabs = {
    [CURRENT]: {
      renderedItems: currentItems,
      condition: true,
    },
    [PREVIOUS]: {
      renderedItems: previousItems,
      condition: caseType !== RELATION,
    },
    [APPLICATIONS]: {
      renderedItems: applicationItems,
      condition: hasApplicationComponents,
    },
  }

  const [tabIndex, setTabIndex] = useState(CURRENT)
  const [showConnectionModal, setShowConnectionModal] = useState(false)
  const [showNoConnectionsListItem, setShowNoConnectionsListItem] = useState(false)

  useEffect(() => {
    activeConnection && setShowConnectionModal(true)
  }, [activeConnection])

  useEffect(() => {
    setShowConnectionModal(Boolean(selectedCase))
  }, [selectedCase])

  useEffect(() => {
    if (activeConnection) {
      setShowNoConnectionsListItem(false)
    } else if (!isCurrentUsersProfile) {
      setShowNoConnectionsListItem(false)
    } else if (items?.length == 0) {
      setShowNoConnectionsListItem(true)
    } else {
      setShowNoConnectionsListItem(false)
    }

  }, [activeConnection, isCasesPending, isCurrentUsersProfile, tabStats])

  useEffect(() => {
    if (activeConnection?.isApplication) {
      setTabIndex(APPLICATIONS)
    }
  }, [showConnectionModal])

  const onChangeTab = (tabIndex) => {
    setTabIndex(tabIndex)
    unsetActiveConnection()
  }

  return (
    <Wrapper>
      <CaseListWrapper>
        {(!hasConfirmedNoConnections || !isCurrentUsersProfile) && (
          <>
            <RegistrationTypeTabs>
              {Object.entries(sectionTabs)
                .filter(([tabType, tab]) => tab.condition)
                .map(([tabType, tab]) => (
                  <RegistrationTypeTabOption
                    key={tabType}
                    className={tabType === tabIndex ? 'active' : ''}
                    onClick={() => onChangeTab(tabType)}
                  >
                    {I18nFunction({ id: `${tabType}` })} (
                    {tab.renderedItems.length})
                  </RegistrationTypeTabOption>
                ))}
            </RegistrationTypeTabs>
            <ProfileCaseList
              items={sectionTabs[tabIndex].renderedItems}
              numberOfTotalItems={items.length}
              isCurrentUsersProfile={isCurrentUsersProfile}
              activeConnection={activeConnection}
              caseType={caseType}
              userId={userId}
              isCasesPending={isCasesPending}
              casePagination={casePagination}
              pageIndex={profileCaseIndex}
              setPageIndex={setProfileCaseIndex}
              editable
            />
          </>
        )
        }
        {showNoConnectionsListItem && (
          <ListItemPlaceholder
            icon={
              hasConfirmedNoConnections ?
                checkmark :
                attentionIcon
            }
            title={
              hasConfirmedNoConnections ?
                I18nFunction({ id: 'connection_form.tabs.revoke_no_relevant_connection_title' }) :
                I18nFunction({ id: 'connection_form.tabs.confirm_no_relevant_connection_title' })
            }
            description={
              hasConfirmedNoConnections ?
                I18nFunction({ id: 'connection_form.tabs.revoke_no_relevant_connection_description' }) :
                I18nFunction({ id: 'connection_form.tabs.confirm_no_relevant_connection_description' })
            }
            clickable={isCurrentUsersProfile}
            onClick={
              hasConfirmedNoConnections ?
                () => cancelConfirmNoConnections(connectionType) :
                () => confirmNoConnections(connectionType)
            }
            pending={
              hasConfirmedNoConnections ?
                isCancelConfirmNoConnectionsPending :
                isConfirmNoConnectionsPending
            }
          />
        )
        }
      </CaseListWrapper>
      <InfoWrapper>
        {(isCurrentUsersProfile && !hasConfirmedNoConnections) && (
          <ActionButtons>
            <AddConnectionButton
              connectionType={connectionType}
              ownershipType={ownershipType}
              userId={userId}
              hasTransientConnection={hasTransientConnection}
              action={addConnection}
            >
              {I18nFunction({ id: 'connection_form.tabs.add_button_text' })}
            </AddConnectionButton>
            {hasApplicationComponents && (
              <AddConnectionButton
                connectionType={connectionType}
                ownershipType={ownershipType}
                userId={userId}
                hasTransientConnection={hasTransientConnection}
                action={applyConnection}
              >
                {I18nFunction({ id: 'connection_form.tabs.apply_for_button_text' })}
              </AddConnectionButton>
            )}
          </ActionButtons>
        )
        }
        <InfoBox>
          <I18n
            id={`connection_form.tabs.${connectionFormTabName}.renderInfo`}
          />
        </InfoBox>
      </InfoWrapper>

      {showConnectionModal && (isCurrentUsersProfile || isAdmin) && (
        <ConnectionModal
          userId={userId}
          unsetActiveConnection={unsetActiveConnection}
          activeConnection={activeConnection}
          item={sectionTabs[tabIndex].renderedItems}
          setShowConnectionModal={setShowConnectionModal}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-top: 1em;
  display: flex;
  count: 2;
`

const CaseListWrapper = styled.div`
  min-width: 70%;
  padding-right: 0.5em
`

const InfoWrapper = styled.div`
  padding-left: 0.5em
`

const RegistrationTypeTabs = styled.div`
  display: flex;
  align-items: center;
`
const RegistrationTypeTabOption = styled.div`
  cursor: pointer;
  font-size: ${({ theme: { largeText } }) => largeText};
  font-weight: bold;

  color: ${({ theme: { subtleText } }) => subtleText};
  border-bottom: 2px solid
    ${({ theme: { secondaryBackground } }) => secondaryBackground};

  padding: 0.25em 0.5em;
  margin-right: 1em;
  &:hover {
    border-bottom: 3px solid
      ${({ theme: { secondaryBackground } }) => secondaryBackground};
  }
  &.active {
    color: ${({ theme: { text } }) => text};
    border-bottom: 3px solid ${({ theme: { primary } }) => primary};
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5em;
  padding: 0.5em 0.5em;
`

const mapStateToProps = (state, props) => {
  const { caseType, connectionKeys, items } = props

  let applicationItems = []
  let previousItems = []
  let currentItems = []

  if (caseType != RELATION) {
    items
      .sort(sortByUpdatedOn)
      .sort((a, b) => sortByWhetherUserConfirmed(a, b, connectionKeys))

    let relevantItems = items?.filter(
      (c) => c[connectionKeys.publicData] || c[connectionKeys.userRegistered],
    )
    previousItems = relevantItems.filter((c) =>
      hasEnded(c[connectionKeys.userRegistered]),
    )
    currentItems = relevantItems.filter(
      (c) => !hasEnded(c[connectionKeys.userRegistered]),
    )
    applicationItems = items?.filter(
      (c) => !c[connectionKeys.publicData] && !c[connectionKeys.userRegistered],
    )
  } else {
    currentItems = items.sort(sortByUpdatedOn)
  }

  let ownershipType = props.connectionType === USER_OWNERSHIP ? 'user_ownership' : undefined

  return {
    previousItems: previousItems,
    currentItems: currentItems,
    applicationItems: applicationItems,
    activeConnection: getActiveConnection(state),
    selectedCase: getSelectedCase(state),
    ownershipType: ownershipType,
    hasConfirmedNoConnections: userHasConfirmedNoConnections(
      state,
      props.userId,
      props.connectionType,
    ),
    isConfirmNoConnectionsPending: isConfirmNoConnectionsPending(
      state,
      props.connectionType,
    ),
    isCancelConfirmNoConnectionsPending: isCancelConfirmNoConnectionsPending(
      state,
      props.connectionType,
    ),
    casePagination: getProfileCasePagination(state),
    profileCaseIndex: getProfileCaseIndex(state),
    isAdmin: isAdmin(state)
  }
}

const mapDispatchToProps = {
  unsetActiveConnection,
  confirmNoConnections,
  cancelConfirmNoConnections,
  applyConnection,
  addConnection,
  setProfileCaseIndex
}

export default connect(mapStateToProps, mapDispatchToProps)(ProfileCaseSection)

const hasEnded = (userRegisteredConnection) =>
  Date.parse(userRegisteredConnection?.toDate) < Date.now()

const sortByUpdatedOn = (a, b) => {
  if (a.updatedOn > b.updatedOn) return -1
  if (a.updatedOn < b.updatedOn) return 1
  return 0
}

const sortByWhetherUserConfirmed = (a, b, connectionKeys) => {
  if (!a[connectionKeys.userRegistered] && b[connectionKeys.userRegistered])
    return -1
  if (a[connectionKeys.userRegistered] && !b[connectionKeys.userRegistered])
    return 1
  return 0
}
